import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';

const GATED_ROOTS = [
  '/career-clarity',
  '/specializations',
  '/job-roles',
  '/ai-pathway',
  '/ai-risk-check',
  '/build-badges',
  '/banking',
];

const ACK_KEY = 'learnixta.educational-note.v2';
const ACK_VALUE = 'acknowledged';
let memoryAck = false;

const normalize = pathname => {
  const clean = (pathname || '/').split('?')[0].split('#')[0];
  return clean.length > 1 ? clean.replace(/\/+$/, '') : '/';
};

const isGated = pathname => {
  const path = normalize(pathname);
  return GATED_ROOTS.some(base => path === base || path.startsWith(base + '/'));
};

const readAck = () => {
  if (memoryAck) return true;
  try { return sessionStorage.getItem(ACK_KEY) === ACK_VALUE; } catch { return false; }
};

const writeAck = () => {
  memoryAck = true;
  try { sessionStorage.setItem(ACK_KEY, ACK_VALUE); } catch { /* in-memory fallback */ }
};

export const PathwayNotice = () => {
  const { pathname } = useLocation();
  const gated = isGated(pathname);
  const [open, setOpen] = useState(false);
  const [understood, setUnderstood] = useState(false);

  useEffect(() => {
    if (!gated) { setOpen(false); setUnderstood(false); return; }
    setOpen(!readAck());
  }, [gated, pathname]);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.querySelector('[data-testid="pathway-notice-heading"]')?.focus();
    const handler = e => {
      if (e.key !== 'Tab') return;
      const items = [...document.querySelectorAll('[data-testid="pathway-notice"] a, [data-testid="pathway-notice"] button:not(:disabled), [data-testid="pathway-notice"] input')];
      if (e.shiftKey && document.activeElement === items[0]) { e.preventDefault(); items.at(-1)?.focus(); }
      else if (!e.shiftKey && document.activeElement === items.at(-1)) { e.preventDefault(); items[0]?.focus(); }
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = previousOverflow;
      previous?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  return <div className="pathway-notice-backdrop" role="dialog" aria-modal="true" aria-labelledby="pathway-notice-title" data-testid="pathway-notice"><section className="pathway-notice-body">
    <ShieldCheck size={30} className="text-teal-700 mb-4" />
    <h2 id="pathway-notice-title" tabIndex={-1} className="font-bold text-lg" data-testid="pathway-notice-heading">A quick note before you explore</h2>
    <p data-testid="pathway-notice-ai">LearnixTa brings education and career information together with AI assistance to help you explore your options. While we aim to make this information useful and clear, LearnixTa can also make mistakes, and some details may change over time.</p>
    <p data-testid="pathway-notice-sources">Use our insights as a starting point. Before making an important decision, confirm details such as eligibility, courses, exams, fees, salaries and application dates with the relevant institution, employer or official source.</p>
    <p data-testid="pathway-notice-context">Career suggestions and future-work insights are intended to support your understanding. Your interests, circumstances and the opportunities available to you also matter.</p>
    <label className="flex gap-3 items-start"><input type="checkbox" checked={understood} onChange={e => setUnderstood(e.target.checked)} data-testid="pathway-notice-checkbox" /><span>I’ve read this note and understand that important details should be confirmed with official sources.</span></label>
    <div className="release-actions"><button className="release-button" disabled={!understood} data-testid="pathway-notice-continue" onClick={() => { writeAck(); setOpen(false); }}>Continue exploring</button><Link to="/" data-testid="pathway-notice-back">Back to pathways</Link></div>
    <div className="release-actions text-sm"><Link to="/ai-disclaimer" data-testid="pathway-notice-disclaimer">AI Disclaimer</Link><Link to="/terms" data-testid="pathway-notice-terms">Terms</Link><Link to="/privacy" data-testid="pathway-notice-privacy">Privacy</Link></div>
  </section></div>;
};
